import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../redux/store";
import { SingleValue, MultiValue } from "react-select";
import EmptyDataOverlay from "../../components/empty-data-overlay";
import Loading from "../../components/loading";
import StyledSelect from "../../components/styled-select";
import Table from "../../components/table";
import TableBodyCell from "../../components/table-body-cell";
import ToastComponent from "../../components/toast";
import PermissionGate from "../../core/layouts/permission-gate";
import { isSelectSingleValue } from "../../core/utils";
import { adminCat } from "../../core/utils/constants";
import paths from "../../navigation/paths";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../redux/features/common-slice";
import {
  getAllCoursesState,
  CourseStatus,
  CourseLanguage,
  CourseLevel,
  allCoursesStateAction,
  CourseResponse,
} from "../../redux/features/course-slice";
import { getUserState } from "../../redux/features/auth-slice";
import { OptionValue } from "../../types/component";
import Button from "../../components/button";
import { UserRole } from "../../redux/features/user-slice";

export default function CoursesPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const allCoursesState = useSelector(getAllCoursesState);
  const errorState = useSelector(getErrorState);
  const userState = useSelector(getUserState);

  const [selectedStatuses, setSelectedStatuses] = useState<CourseStatus[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<CourseLanguage[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<CourseLevel[]>([]);

  const fetchAllCourses = useCallback(async () => {
    await dispatch(
      allCoursesStateAction({
        status: selectedStatuses,
        language: selectedLanguages,
        level: selectedLevels,
      })
    );
  }, [dispatch, selectedStatuses, selectedLanguages, selectedLevels]);

  useEffect(() => {
    fetchAllCourses();
  }, [fetchAllCourses]);

  useEffect(() => {
    dispatch(
      setBreadcrumbState([{ id: "courses", path: null, title: "Courses" }])
    );
  }, [dispatch]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
      } else {
        toast(errorState.message, { type: "error" });
      }
    }
  }, [errorState, navigate]);

  // Determine if the user is an admin
  const isAdmin = (userState?.data?.role === UserRole.Admin) || (userState?.data?.role === UserRole.Trainer);

  // Filter courses for non-admin users to only show courses where they are an author
  const displayedCourses = isAdmin
    ? allCoursesState.data
    : allCoursesState.data.filter((course) =>
        course.authors.some((author) => author.id === userState?.data?.id)
      );

  return (
    <PermissionGate categories={[]} roles={[]}>
      <Helmet title="All Courses" />
      <div className="flex items-center justify-between mb-4">
        <Button
          label="Add course"
          onClick={() => navigate(paths.ADD_COURSE)}
          variant="filled"
        />
        <div className="flex space-x-2">
          <StyledSelect
            options={Object.values(CourseStatus).map((e: string) => ({
              value: e,
              label: e.charAt(0).toUpperCase() + e.slice(1),
            }))}
            isClearable={false}
            value={{
              value: "",
              label:
                selectedStatuses.length > 0
                  ? `${selectedStatuses.length} status${
                      selectedStatuses.length > 1 ? "es" : ""
                    } selected`
                  : "All statuses",
            }}
            onChange={(
              value: SingleValue<OptionValue> | MultiValue<OptionValue>
            ) => {
              if (value && isSelectSingleValue(value)) {
                if (selectedStatuses.includes(value.value as CourseStatus)) {
                  setSelectedStatuses(
                    selectedStatuses.filter((e) => e !== value.value)
                  );
                } else {
                  setSelectedStatuses([
                    ...selectedStatuses,
                    value.value as CourseStatus,
                  ]);
                }
              }
            }}
            exClassName="w-48 z-20"
            exOption={(value: OptionValue) => ({
              background:
                selectedStatuses.findIndex((e) => e === value.value) !== -1
                  ? "#f1f5f9"
                  : undefined,
            })}
          />
          <StyledSelect
            options={Object.values(CourseLanguage).map((e: string) => ({
              value: e,
              label: e.charAt(0).toUpperCase() + e.slice(1),
            }))}
            isClearable={false}
            value={{
              value: "",
              label:
                selectedLanguages.length > 0
                  ? `${selectedLanguages.length} language${
                      selectedLanguages.length > 1 ? "s" : ""
                    } selected`
                  : "All languages",
            }}
            onChange={(
              value: SingleValue<OptionValue> | MultiValue<OptionValue>
            ) => {
              if (value && isSelectSingleValue(value)) {
                if (selectedLanguages.includes(value.value as CourseLanguage)) {
                  setSelectedLanguages(
                    selectedLanguages.filter((e) => e !== value.value)
                  );
                } else {
                  setSelectedLanguages([
                    ...selectedLanguages,
                    value.value as CourseLanguage,
                  ]);
                }
              }
            }}
            exClassName="w-48 z-20"
            exOption={(value: OptionValue) => ({
              background:
                selectedLanguages.findIndex((e) => e === value.value) !== -1
                  ? "#f1f5f9"
                  : undefined,
            })}
          />
          <StyledSelect
            options={Object.values(CourseLevel).map((e: string) => ({
              value: e,
              label: e.charAt(0).toUpperCase() + e.slice(1),
            }))}
            isClearable={false}
            value={{
              value: "",
              label:
                selectedLevels.length > 0
                  ? `${selectedLevels.length} level${
                      selectedLevels.length > 1 ? "s" : ""
                    } selected`
                  : "All levels",
            }}
            onChange={(
              value: SingleValue<OptionValue> | MultiValue<OptionValue>
            ) => {
              if (value && isSelectSingleValue(value)) {
                if (selectedLevels.includes(value.value as CourseLevel)) {
                  setSelectedLevels(
                    selectedLevels.filter((e) => e !== value.value)
                  );
                } else {
                  setSelectedLevels([
                    ...selectedLevels,
                    value.value as CourseLevel,
                  ]);
                }
              }
            }}
            exClassName="w-48 z-20"
            exOption={(value: OptionValue) => ({
              background:
                selectedLevels.findIndex((e) => e === value.value) !== -1
                  ? "#f1f5f9"
                  : undefined,
            })}
          />
        </div>
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 64px)",
        }}
        headerCells={[
          { id: "name", label: "Name", align: "text-left", width: "w-3/12" },
          {
            id: "authors",
            label: "Authors",
            align: "text-left",
            width: "w-3/12",
          },
          {
            id: "prerequisite",
            label: "Prerequisite",
            align: "text-left",
            width: "w-2/12",
          },
          {
            id: "capacity",
            label: "Capacity",
            align: "text-left",
            width: "w-1/12",
          },
          { id: "level", label: "Level", align: "text-left", width: "w-2/12" },
        ]}
        body={
          allCoursesState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : displayedCourses.length > 0
            ? displayedCourses.map((course: CourseResponse) => (
                <tr
                  key={course.id}
                  className="border-t border-dashed border-divider cursor-pointer hover:bg-action-hover"
                  onClick={() => navigate(`${paths.COURSES}/${course.id}`)}
                >
                  <TableBodyCell>
                    <label className="whitespace-normal break-words hover:underline cursor-pointer">
                      {course.name}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {course.authors.map((author) => author.name).join(", ") ||
                        "--"}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {course.prerequisite || "--"}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {course.capacity}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words capitalize">
                      {course.level}
                    </label>
                  </TableBodyCell>
                </tr>
              ))
            : [
                <tr key="empty-row">
                  <td colSpan={6} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="Contact People Of Culture Team to create your own course"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="allCourses" />
    </PermissionGate>
  );
}
